import {
  LockOutlined,
  LockOpenOutlined,
  PersonRemoveOutlined,
  EditOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grow,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import api from "../../../../services/api";

// tipo: 0=Todos, 1=Exceto, 2=Somente
const TIPO_TODOS = 0;
const TIPO_EXCETO = 1;
const TIPO_SOMENTE = 2;

const OPCOES = [
  {
    value: TIPO_TODOS,
    label: "Todos",
    descricao: "Todos os usuários têm acesso a este mapa",
    icon: <LockOpenOutlined fontSize="small" />,
  },
  {
    value: TIPO_EXCETO,
    label: "Exceto",
    descricao: "Todos os usuários têm acesso, exceto os selecionados",
    icon: <PersonRemoveOutlined fontSize="small" />,
  },
  {
    value: TIPO_SOMENTE,
    label: "Somente",
    descricao: "Somente os usuários selecionados têm acesso",
    icon: <LockOutlined fontSize="small" />,
  },
];

export default function ModalAcessosMapa({
  id = "",
  mapName = "",
  openModal = false,
  setOpenModal = () => {},
  mapAccess = null,
  setSnackbar = () => {},
  updateMapAccessInMaps = () => {},
  currentUserId = "",
}) {
  const theme = useTheme();
  const [tipo, setTipo] = useState(TIPO_TODOS);
  const [usuarios, setUsuarios] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [prevTipo, setPrevTipo] = useState(TIPO_TODOS);
  const [prevUsuarios, setPrevUsuarios] = useState([]);

  const styles = {
    dialog: { width: { md: "520px" }, minWidth: "280px" },
    toggleGroup: { width: "100%", gap: "8px" },
    toggleButton: {
      flex: 1,
      flexDirection: "column",
      gap: "4px",
      py: 1.5,
      borderRadius: "8px !important",
      border: `1px solid ${theme.palette.divider} !important`,
      "&.Mui-selected": {
        borderColor: `${theme.palette.primary.main} !important`,
        backgroundColor: `${theme.palette.primary.main}18`,
      },
    },
    infoBox: {
      p: 1.5,
      borderRadius: "8px",
      backgroundColor: theme.palette.background.cardStatus,
    },
  };

  useEffect(() => {
    if (openModal) {
      setEditMode(false);
      fetchMapAccess();
      fetchUsers();
    }
  }, [openModal]);

  async function fetchMapAccess() {
    try {
      setLoadingAccess(true);
      const response = await api.post("/easyMaps/map/access/find", { mapsId: id });
      const access = response.data || null;
      const t = access?.tipo ?? TIPO_TODOS;
      const saved = (access?.usuarios ?? [])
        .map((u) => u.usuarioCliente)
        .filter(Boolean);
      setTipo(t);
      setUsuarios(saved);
      setPrevTipo(t);
      setPrevUsuarios(saved);
    } catch (error) {
      console.error(error);
      // fallback para o prop caso a requisição falhe
      const t = mapAccess?.tipo ?? TIPO_TODOS;
      setTipo(t);
      setPrevTipo(t);
      setUsuarios([]);
      setPrevUsuarios([]);
    } finally {
      setLoadingAccess(false);
    }
  }

  async function fetchUsers() {
    try {
      setLoadingUsers(true);
      const response = await api.get("/easyMaps/map/access/users");
      // Remove o próprio usuário da lista — ele não pode remover seu próprio acesso
      const filtered = (response.data || []).filter((u) => u.id !== currentUserId);
      setAllUsers(filtered);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingUsers(false);
    }
  }

  async function handleSave() {
    try {
      setLoading(true);
      const response = await api.put("/easyMaps/map/access/upsert", {
        mapsId: id,
        tipo,
        usuarioIds: tipo === TIPO_TODOS ? [] : usuarios.map((u) => u.id),
      });

      if (response.data) {
        updateMapAccessInMaps(id, response.data);
        setSnackbar({ children: "Acessos salvos com sucesso!", severity: "success" });
        setEditMode(false);
        setPrevTipo(tipo);
        setPrevUsuarios(usuarios);
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: `Erro: ${error?.message || "Não foi possível salvar os acessos"}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setOpenModal(false);
    setEditMode(false);
    setTipo(prevTipo);
    setUsuarios(prevUsuarios);
  }

  function handleCancelEdit() {
    setEditMode(false);
    setTipo(prevTipo);
    setUsuarios(prevUsuarios);
  }

  const opcaoAtual = OPCOES.find((o) => o.value === tipo);
  const showUserSelect = tipo !== TIPO_TODOS;

  const getUserLabel = (u) => {
    const nome = u?.PerfilCliente?.nome || u?.PerfilCliente?.nomeFantasia;
    return nome ? `${u.login} — ${nome}` : u.login;
  };

  return (
    <Dialog open={openModal} onClose={handleClose}>
      <DialogContent sx={styles.dialog}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <DialogTitle sx={{ p: 0, pb: 1 }}>{`Acessos — "${mapName}"`}</DialogTitle>
          {!editMode && !loadingAccess && (
            <Tooltip title="Editar acessos">
              <Button
                size="small"
                startIcon={<EditOutlined />}
                onClick={() => {
                  setPrevTipo(tipo);
                  setPrevUsuarios(usuarios);
                  setEditMode(true);
                }}
              >
                Editar
              </Button>
            </Tooltip>
          )}
        </Stack>

        <Stack gap={2}>
          <DialogContentText>
            Defina quem pode visualizar este mapa
          </DialogContentText>

          {loadingAccess ? (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={28} />
            </Stack>
          ) : editMode ? (
            <Grow in={editMode}>
              <Stack gap={2}>
                <ToggleButtonGroup
                  value={tipo}
                  exclusive
                  onChange={(_, val) => {
                    if (val !== null) {
                      setTipo(val);
                      if (val === TIPO_TODOS) setUsuarios([]);
                    }
                  }}
                  sx={styles.toggleGroup}
                >
                  {OPCOES.map((op) => (
                    <ToggleButton key={op.value} value={op.value} sx={styles.toggleButton}>
                      {op.icon}
                      <Typography variant="body2" fontWeight={600}>
                        {op.label}
                      </Typography>
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {showUserSelect && (
                  <Grow in={showUserSelect}>
                    <Autocomplete
                      multiple
                      fullWidth
                      disableCloseOnSelect
                      loading={loadingUsers}
                      options={allUsers}
                      value={usuarios}
                      onChange={(_, newValue) => setUsuarios(newValue)}
                      getOptionLabel={getUserLabel}
                      isOptionEqualToValue={(opt, val) => opt.id === val.id}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {getUserLabel(option)}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label={
                            tipo === TIPO_EXCETO
                              ? "Usuários sem acesso"
                              : "Usuários com acesso"
                          }
                        />
                      )}
                    />
                  </Grow>
                )}
              </Stack>
            </Grow>
          ) : (
            <Grow in={!editMode}>
              <Stack gap={1.5}>
                <Box sx={styles.infoBox}>
                  <Stack direction="row" gap={1.5} alignItems="center">
                    {opcaoAtual?.icon}
                    <Stack>
                      <Typography variant="body1" fontWeight={600}>
                        {opcaoAtual?.label}
                      </Typography>
                      <DialogContentText variant="body2">
                        {opcaoAtual?.descricao}
                      </DialogContentText>
                    </Stack>
                  </Stack>
                </Box>

                {showUserSelect && usuarios.length > 0 && (
                  <>
                    <DialogContentText variant="body2">
                      {tipo === TIPO_EXCETO ? "Usuários bloqueados:" : "Usuários permitidos:"}
                    </DialogContentText>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                    >
                      {usuarios.map((u) => (
                        <Chip
                          key={u.id}
                          size="small"
                          color={tipo === TIPO_EXCETO ? "error" : "primary"}
                          label={getUserLabel(u)}
                        />
                      ))}
                    </Box>
                  </>
                )}

                {showUserSelect && usuarios.length === 0 && (
                  <DialogContentText variant="body2" color="warning.main">
                    Nenhum usuário selecionado — configure antes de salvar.
                  </DialogContentText>
                )}
              </Stack>
            </Grow>
          )}
        </Stack>

        <Divider sx={{ mt: 2 }} />

        <DialogActions sx={{ px: 0, pt: 2 }}>
          {editMode ? (
            <Stack direction="row" gap={1.5}>
              <Button variant="outlined" onClick={handleCancelEdit}>
                Cancelar
              </Button>
              <LoadingButton
                loading={loading}
                variant="contained"
                disabled={showUserSelect && usuarios.length === 0}
                onClick={handleSave}
              >
                Salvar
              </LoadingButton>
            </Stack>
          ) : (
            <Button variant="contained" onClick={handleClose}>
              Concluir
            </Button>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
